/**
 * Page titles — card 41957.
 *
 * Every route in this SPA used to serve the ONE `<title>` baked into `web/index.html`,
 * so nine visibly different pages shared a single tab label. That is a WCAG 2.4.2
 * ("Page Titled", Level A) failure, and it also made `<title>` useless as a route
 * discriminator — `docs/oval/LEARNINGS.md:12505` records the SPA's single title turning
 * a route assertion into "an instrument that cannot fail", and :8085 records `ab open`
 * printing the correct page title while actually sitting on `/login`.
 *
 * This module is deliberately pure and free of React: jest here is `testEnvironment: 'node'`
 * with `transform: {}`, so a `.jsx` or a React import would make it unloadable by the suite.
 * The effect that writes `document.title` lives in `web/hooks/useDocumentTitle.js`.
 */

export const BASE_DOCUMENT_TITLE = 'Oval Care Online Telehealth & Pharmacy';

export const BRAND_SUFFIX = ' | Oval Care';

export const SANDBOX_TITLE_PREFIX = '[SANDBOX] ';

/**
 * Pure: given a title and an environment, return the title that should be shown.
 * Idempotent — applying it twice cannot produce "[SANDBOX] [SANDBOX] ".
 *
 * Lives here rather than beside its effect in `useDocumentTitle.js` so the suite can
 * load it: that module imports React and `react-router-dom`, which this jest config
 * (`testEnvironment: 'node'`, `transform: {}`) cannot resolve. It is re-exported from
 * the hook module, so existing importers are unaffected.
 */
export const formatDocumentTitle = (title, isSandbox) => {
  const bare = title.startsWith(SANDBOX_TITLE_PREFIX)
    ? title.slice(SANDBOX_TITLE_PREFIX.length)
    : title;
  return isSandbox ? `${SANDBOX_TITLE_PREFIX}${bare}` : bare;
};

/**
 * Exact pathname -> the topic the tab should name.
 *
 * Dynamic routes (`/products/:slug`, `/categories/:slug`, `/offers/:slug`, `/blog/:slug`,
 * `/gift/:code`) are DELIBERATELY absent: their titles depend on fetched data, and a
 * placeholder tab reading "Product" on every product is worse than the brand title.
 * They fall through to `BASE_DOCUMENT_TITLE` until a page sets its own title.
 */
export const ROUTE_TITLES = Object.freeze({
  '/treatments': 'Treatments',
  '/states': 'States We Serve',
  '/login': 'Log In',
  '/register': 'Register',
  '/terms': 'Terms of Use',
  '/privacy': 'Privacy Policy',
  '/hipaa': 'HIPAA Notice of Privacy Practices',
  '/returns': 'Returns & Refund Policy',
  '/categories': 'Categories',
  '/blog': 'Blog',
  '/partners': 'Partners',
  '/groups': 'Groups',
  '/api-docs': 'API Docs',
  '/affiliates': 'Affiliates',
  '/telehealth-consent': 'Telehealth Consent',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/dashboard': 'Dashboard',
  '/catalog': 'Catalog',
  '/orders': 'Orders',
  '/profile': 'Profile',
  '/notifications': 'Notifications',
  '/resources': 'Resources',
  '/support': 'Support',
  '/checkout': 'Checkout'
});

/**
 * Pure: given a pathname, return the document title for it.
 *
 * A trailing slash is normalised away so `/terms/` and `/terms` agree. An unmapped path
 * — which includes every dynamic route and any 404 — returns the brand title, so the tab
 * can never read `undefined` or an empty string.
 */
export const derivePageTitle = (pathname) => {
  if (typeof pathname !== 'string' || pathname.length === 0) return BASE_DOCUMENT_TITLE;

  const normalised = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const topic = ROUTE_TITLES[normalised];

  return topic ? `${topic}${BRAND_SUFFIX}` : BASE_DOCUMENT_TITLE;
};

export default derivePageTitle;
